// src/services/sales.service.ts
import axios from 'axios';
import {
  SalesOrdersService,
  SalesOrdersMixedBranchFulfillmentService,
  CustomerProfilesService,
  LocationsService,
  SalesOrderPaymentsService,
  SalesOrderInstallmentsService,
  WarehouseInventoryProductsService,
  CreateMixedSaleDto,
  CreateSalesOrderWithItemsDto,
  CreateSalesOrderPaymentDto,
  CreateSalesOrderInstallmentDto,
  GenerateInstallmentsDto,
  CreateCustomerProfileDto,
  UpdateCustomerProfileDto,
  PaymentSummaryDto,
  CustomerProfile as APICustomerProfile,
  SalesOrder as APISalesOrder,
  Location as APILocation,
  ReturnSalesOrderDto,
  PartialReturnSalesOrderDto,
} from '../api';
import { OpenAPI } from '../api/core/OpenAPI';
import { store } from '../redux/store';
import { mapApiPaymentToLocal } from '../utils/sales/mapApiPaymentToLocal';

import {
  CustomerProfile,
  CustomerPostSaleStatus,
  CustomerSalesHistoryItem,
  SalesOrder,
  Location,
  SalesOrderItem,
  SalesOrderPayment,
  WarehouseInventoryProduct,
  SaleFulfillmentDetail,
  ReturnFormData,
} from '../types/sales.types';
import { SalesOrderResponseDto } from '../api';

function toReturnSalesOrderDto(data: ReturnFormData): ReturnSalesOrderDto {
  return {
    returnReason: data.returnReason as ReturnSalesOrderDto.returnReason,
    returnNotes: data.returnNotes,
    itemCondition: data.itemCondition as ReturnSalesOrderDto.itemCondition,
    applyRestockingFee: data.applyRestockingFee,
    restockingFeePercentage: data.restockingFeePercentage,
    refundReference: data.refundReference,
    refundNotes: data.refundNotes,
    customRefundAmount: data.customRefundAmount,
  };
}

// Simple mapping functions to convert API types to local types
const mapAPICustomerToLocal = (apiCustomer: APICustomerProfile): CustomerProfile => ({
  id: apiCustomer.id,
  customerName: apiCustomer.customerName,
  identityDocumentNumber: apiCustomer.identityDocumentNumber as unknown as string,
  customerEmail: apiCustomer.customerEmail,
  customerPhone: apiCustomer.customerPhone,
  referenceNumber1: (apiCustomer.referenceNumber1 as unknown as string) ?? null,
  referenceNumber2: (apiCustomer.referenceNumber2 as unknown as string) ?? null,
  customerAddress: apiCustomer.customerAddress,
  dateOfBirth: apiCustomer.dateOfBirth ? new Date(apiCustomer.dateOfBirth) : undefined,
  gender: apiCustomer.gender as CustomerProfile['gender'],
  totalPurchases: apiCustomer.totalPurchases ?? 0,
  purchaseCount: apiCustomer.purchaseCount ?? 0,
  loyaltyPoints: apiCustomer.loyaltyPoints ?? 0,
  loyaltyTier: (apiCustomer.loyaltyTier ?? 'BRONZE') as CustomerProfile['loyaltyTier'],
  preferredPaymentMethod: apiCustomer.preferredPaymentMethod,
  customerNotes: apiCustomer.customerNotes,
  marketingConsent: Boolean(apiCustomer.marketingConsent),
  referralSource: apiCustomer.referralSource,
  createdAt: new Date(apiCustomer.createdAt ?? Date.now()),
  updatedAt: new Date(apiCustomer.updatedAt ?? Date.now()),
});

const mapAPILocationToLocal = (apiLocation: APILocation): Location => ({
  id: apiLocation.id,
  name: apiLocation.name,
  address: apiLocation.address,
  type: 'STORE' as const,
  isActive: true,
  createdAt: new Date(),
  updatedAt: new Date(),
});

// Map an API order item (with extra fields present at runtime) to local type
const mapAPIOrderItemToLocal = (apiItem: any, localOrder: SalesOrder): SalesOrderItem => ({
  id: apiItem.id,
  order: localOrder,
  warehouseInventoryProduct:
    apiItem.warehouseInventoryProduct as unknown as WarehouseInventoryProduct,
  originalPrice: Number(apiItem.originalPrice),
  markupType: (apiItem.markupType ?? 'NONE') as SalesOrderItem['markupType'],
  markupValue: apiItem.markupValue != null ? Number(apiItem.markupValue) : 0,
  adjustedPrice:
    apiItem.adjustedPrice != null ? Number(apiItem.adjustedPrice) : Number(apiItem.originalPrice),
  discountType: apiItem.discountType,
  discountValue: apiItem.discountValue != null ? Number(apiItem.discountValue) : undefined,
  finalPrice: Number(apiItem.finalPrice),
  itemDiscountApplied: Boolean(apiItem.itemDiscountApplied),
  fulfillmentType: apiItem.fulfillmentType ?? 'LOCAL',
  sourceLocation: apiItem.sourceLocation
    ? mapAPILocationToLocal(apiItem.sourceLocation)
    : apiItem.sourceLocation,
  isDelivered: Boolean(apiItem.isDelivered),
  deliveredAt: apiItem.deliveredAt ? new Date(apiItem.deliveredAt) : null,
});

const mapAPIPaymentToLocal = mapApiPaymentToLocal;

const mapAPISalesOrderToLocal = (apiOrder: APISalesOrder): SalesOrder => {
  // Build base order first
  const base: SalesOrder = {
    id: apiOrder.id,
    orderCode: apiOrder.orderCode,
    branchReceiptCode: apiOrder.branchReceiptCode ?? null,
    location: mapAPILocationToLocal(apiOrder.location),
    createdBy: {
      id: 0,
      username: 'system',
      email: '',
      firstName: '',
      lastName: '',
      role: 'USER',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as any, // Default user object
    customer: apiOrder.customer ? mapAPICustomerToLocal(apiOrder.customer) : undefined,
    status: apiOrder.status as any,
    fulfillmentStatus: ((apiOrder as any).fulfillmentStatus ?? 'NOT_REQUIRED') as any,
    deliveryStatus: ((apiOrder as any).deliveryStatus ?? 'NONE') as any,
    salesChannel: apiOrder.salesChannel as any,
    priorityLevel: apiOrder.priorityLevel as any,
    hasDiscount: apiOrder.hasDiscount,
    discountScope: apiOrder.discountScope as any,
    hasPartialPayment: apiOrder.hasPartialPayment,
    partialPaymentScope: apiOrder.partialPaymentScope as any,
    hasInstallments: apiOrder.hasInstallments,
    installmentsFrequency: apiOrder.installmentsFrequency as any,
    subtotal: Number((apiOrder as any).subtotal),
    discountAmount: Number((apiOrder as any).discountAmount),
    subtotalBeforeTax: Number((apiOrder as any).subtotalBeforeTax),
    totalWithTax:
      (apiOrder as any).totalWithTax != null ? Number((apiOrder as any).totalWithTax) : undefined,
    totalAmount: Number((apiOrder as any).totalAmount),
    extraChargesTotal: Number((apiOrder as any).extraChargesTotal ?? 0),
    hasExtraCharges: Boolean((apiOrder as any).hasExtraCharges),
    hasInvoice: Boolean((apiOrder as any).hasInvoice),
    totalCost: Number((apiOrder as any).totalCost ?? 0),
    profitMargin: Number((apiOrder as any).profitMargin ?? 0),
    orderNotes: apiOrder.orderNotes ?? undefined,
    customerNotes: apiOrder.customerNotes ?? undefined,
    estimatedCompletion: apiOrder.estimatedCompletion
      ? new Date(apiOrder.estimatedCompletion)
      : undefined,
    lastPaymentDate: apiOrder.lastPaymentDate ? new Date(apiOrder.lastPaymentDate) : undefined,
    nextPaymentDue: apiOrder.nextPaymentDue ? new Date(apiOrder.nextPaymentDue) : undefined,
    saleDate: new Date(apiOrder.saleDate ?? apiOrder.createdAt ?? Date.now()),
    createdAt: new Date(apiOrder.createdAt ?? Date.now()),
    updatedAt: new Date(apiOrder.updatedAt ?? Date.now()),
    items: [],
    payments: [],
    installments: [],
    receipts: [],
    taxes: [],
    discounts: [],
    charges: [],
  };

  const apiCharges: any[] = Array.isArray((apiOrder as any).charges)
    ? ((apiOrder as any).charges as any[])
    : [];
  if (apiCharges.length > 0) {
    base.charges = apiCharges.map((charge) => ({
      id: charge.id,
      categoryCode: charge.categoryCode,
      categoryLabel: charge.category?.label ?? charge.categoryLabel ?? charge.categoryCode,
      amount: Number(charge.amount),
      description: charge.description ?? null,
    }));
  }

  // Map items when present (complete order endpoint)
  const apiItems: any[] = Array.isArray((apiOrder as any).items)
    ? ((apiOrder as any).items as any[])
    : [];
  if (apiItems.length > 0) {
    base.items = apiItems.map((it) => mapAPIOrderItemToLocal(it, base));
  }

  // Map payments when present (complete order endpoint). Keep [] so callers
  // can trust "included and empty" vs "omitted" without a second payments GET.
  if (Array.isArray((apiOrder as any).payments)) {
    base.payments = ((apiOrder as any).payments as any[]).map((p) => mapAPIPaymentToLocal(p, base));
  }

  return base;
};

const isPostSaleStatus = (v: unknown): v is CustomerPostSaleStatus =>
  v === 'NONE' || v === 'REFUNDED' || v === 'EXCHANGED' || v === 'REFUNDED_AND_EXCHANGED';

function mapCustomerSalesHistoryRow(row: unknown): CustomerSalesHistoryItem {
  if (row == null || typeof row !== 'object') {
    throw new Error('Invalid sales history row');
  }
  const r = row as Record<string, unknown>;
  const post = r.postSaleStatus;
  const createdRaw = String(r.createdAt ?? '');
  const saleRaw = r.saleDate != null ? String(r.saleDate) : createdRaw;
  return {
    orderId: Number(r.orderId),
    orderCode: String(r.orderCode ?? ''),
    branchReceiptCode:
      r.branchReceiptCode == null || r.branchReceiptCode === ''
        ? null
        : String(r.branchReceiptCode),
    saleDate: new Date(saleRaw),
    createdAt: new Date(createdRaw),
    totalAmount: Number(r.totalAmount ?? 0),
    orderStatus: String(r.orderStatus ?? ''),
    locationName: r.locationName == null ? null : String(r.locationName),
    hasRefund: Boolean(r.hasRefund),
    hasExchange: Boolean(r.hasExchange),
    postSaleStatus: isPostSaleStatus(post) ? post : 'NONE',
  };
}

type SellableCatalogPage = {
  data: WarehouseInventoryProduct[];
  page: number;
  limit: number;
  hasMore: boolean;
};

/** Branch stock (ASSIGNED) and optionally warehouse stock (UNASSIGNED) for mixed sales. */
async function fetchSellableCatalogPage(
  status: 'ASSIGNED' | 'UNASSIGNED',
  locationId: number | undefined,
  page: number,
  limit: number,
  searchTerm: string | undefined,
): Promise<SellableCatalogPage> {
  const response =
    await WarehouseInventoryProductsService.warehouseInventoryProductsControllerFindAll(
      page,
      limit,
      undefined, // productCode
      status,
      'AVAILABLE', // availabilityStatus
      locationId,
      undefined, // transferredToBranch
      undefined, // productCodeUnique
      undefined, // productName
      searchTerm,
      undefined, // categoryName
      undefined, // createdAtStart
      undefined, // createdAtEnd
      undefined, // recordCode
      undefined, // searchTag
      undefined, // materialId
      undefined, // diameterSize
      undefined, // color
      undefined, // workshopCode
      undefined, // weightGramsMin
      undefined, // weightGramsMax
      undefined, // salePriceMin
      undefined, // salePriceMax
      undefined, // salePricePerGramMin
      undefined, // salePricePerGramMax
      'productName', // sortBy
      'ASC', // sortOrder
      undefined, // excludeCancelledAvailability
      undefined, // warehousePlacementOnly
      undefined, // assignedPlacementOnly
    );

  const data = Array.isArray(response?.data) ? response.data : [];
  const totalPagesRaw =
    typeof response?.meta?.totalPages === 'number'
      ? response.meta.totalPages
      : typeof response?.totalPages === 'number'
        ? response.totalPages
        : undefined;
  const hasMore = totalPagesRaw != null ? page < totalPagesRaw : data.length >= limit;

  return { data, page, limit, hasMore };
}

function mergeSellableCatalogPages(
  branch: SellableCatalogPage,
  warehouse: SellableCatalogPage,
): SellableCatalogPage {
  const byId = new Map<number, WarehouseInventoryProduct>();
  for (const product of branch.data) {
    byId.set(product.id, product);
  }
  for (const product of warehouse.data) {
    byId.set(product.id, product);
  }
  const data = Array.from(byId.values()).sort((a, b) => {
    const nameA = a.product?.name ?? '';
    const nameB = b.product?.name ?? '';
    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
  });
  return {
    data,
    page: branch.page,
    limit: branch.limit,
    hasMore: branch.hasMore || warehouse.hasMore,
  };
}

export const salesApi = {
  getAvailableProductsPaginated: async (
    locationId?: number,
    options?: {
      page?: number;
      limit?: number;
      query?: string;
      /** When true (mixed-sale catalog), also include UNASSIGNED warehouse pieces. */
      includeWarehouse?: boolean;
    },
  ): Promise<SellableCatalogPage> => {
    const page = options?.page ?? 1;
    const limit = options?.limit ?? 50;
    const query = options?.query?.trim() ?? '';
    const searchTerm = query !== '' ? query : undefined;
    const includeWarehouse = options?.includeWarehouse === true;

    try {
      const branchPage = await fetchSellableCatalogPage(
        'ASSIGNED',
        locationId,
        page,
        limit,
        searchTerm,
      );

      if (!includeWarehouse) {
        return branchPage;
      }

      // Warehouse pieces live as UNASSIGNED at WAREHOUSE locations.
      const warehousePage = await fetchSellableCatalogPage(
        'UNASSIGNED',
        undefined,
        page,
        limit,
        searchTerm,
      );
      warehousePage.data = warehousePage.data.filter(
        (product) => product.location?.type === 'WAREHOUSE',
      );

      return mergeSellableCatalogPages(branchPage, warehousePage);
    } catch (error) {
      console.error('Error fetching available products (paginated):', error);
      throw error;
    }
  },

  // Customer Profile endpoints
  getCustomersPaginated: async (params: {
    page: number;
    limit: number;
    searchTerm?: string;
    email?: string;
    phone?: string;
    loyaltyTier?: string;
    saleDateFrom?: string;
    saleDateTo?: string;
  }): Promise<{
    data: CustomerProfile[];
    total: number;
    page: number;
    limit: number;
  }> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerFindAll(
        params.page,
        params.limit,
        params.searchTerm,
        params.email,
        params.phone,
        params.loyaltyTier as 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | undefined,
        params.saleDateFrom,
        params.saleDateTo,
      );
      return {
        data: response.data.map(mapAPICustomerToLocal),
        total: response.total,
        page: response.page,
        limit: response.limit,
      };
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  },

  getCustomerById: async (id: number): Promise<CustomerProfile> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerFindOne(id);
      return mapAPICustomerToLocal(response);
    } catch (error) {
      console.error(`Error fetching customer ${id}:`, error);
      throw error;
    }
  },

  searchCustomers: async (query: string): Promise<CustomerProfile[]> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerSearch(query);
      return response.map(mapAPICustomerToLocal);
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  },

  createCustomer: async (customer: CreateCustomerProfileDto): Promise<CustomerProfile> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerCreate(customer);
      return mapAPICustomerToLocal(response);
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  updateCustomer: async (
    id: number,
    customer: UpdateCustomerProfileDto,
  ): Promise<CustomerProfile> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerUpdate(id, customer);
      return mapAPICustomerToLocal(response);
    } catch (error) {
      console.error(`Error updating customer ${id}:`, error);
      throw error;
    }
  },

  deleteCustomer: async (id: number): Promise<void> => {
    try {
      await CustomerProfilesService.customerProfilesControllerRemove(id);
    } catch (error) {
      console.error(`Error deleting customer ${id}:`, error);
      throw error;
    }
  },

  // Location endpoints
  getLocations: async (): Promise<Location[]> => {
    try {
      const response = await LocationsService.locationsControllerFindAll();
      return response.map(mapAPILocationToLocal);
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  getLocationById: async (id: number): Promise<Location> => {
    try {
      const response = await LocationsService.locationsControllerFindOne(id.toString());
      return mapAPILocationToLocal(response);
    } catch (error) {
      console.error(`Error fetching location ${id}:`, error);
      throw error;
    }
  },

  // Warehouse Inventory Products endpoints (for sales search)
  getAvailableProducts: async (locationId?: number, options?: { includeWarehouse?: boolean }) => {
    try {
      const response = await salesApi.getAvailableProductsPaginated(locationId, {
        page: 1,
        limit: 100,
        includeWarehouse: options?.includeWarehouse,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching available products:', error);
      throw error;
    }
  },

  searchProducts: async (
    query: string,
    locationId?: number,
    options?: { includeWarehouse?: boolean },
  ) => {
    try {
      const response = await salesApi.getAvailableProductsPaginated(locationId, {
        page: 1,
        limit: 50,
        query,
        includeWarehouse: options?.includeWarehouse,
      });
      return response.data;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },

  // Sales Orders endpoints
  getSalesOrders: async (): Promise<SalesOrderResponseDto[]> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindAll();
      return response.data; // Return DTOs directly
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      throw error;
    }
  },

  // New method for filtered sales orders with pagination
  getSalesOrdersWithFilters: async (filters: {
    page?: number;
    limit?: number;
    orderCode?: string;
    branchReceiptCode?: string;
    status?: string;
    locationId?: number;
    customerId?: number;
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
    customerSearchTerm?: string;
    customerPhone?: string;
  }): Promise<{
    data: SalesOrderResponseDto[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindAll(
        filters.page ?? 1,
        filters.limit ?? 10,
        filters.orderCode,
        filters.branchReceiptCode,
        filters.status as any,
        undefined, // salesChannel
        undefined, // priorityLevel
        filters.locationId,
        filters.customerId,
        undefined, // createdById
        filters.startDate,
        filters.endDate,
        undefined, // lastPaymentDateFrom
        undefined, // lastPaymentDateTo
        undefined, // nextPaymentDueFrom
        undefined, // nextPaymentDueTo
        undefined, // minTotalAmount
        undefined, // maxTotalAmount
        undefined, // hasDiscount
        undefined, // hasPartialPayment
        undefined, // hasInstallments
        filters.searchTerm,
        filters.customerSearchTerm,
        filters.customerPhone,
        undefined, // createdInLastHours
        undefined, // isOverdue
        false, // includeItems
        false, // includePayments
        false, // includeReceipts
      );

      return {
        data: response.data, // Return DTOs directly
        pagination: {
          page: response.meta?.page ?? 1,
          limit: response.meta?.limit ?? 10,
          total: response.meta?.total ?? 0,
          totalPages: response.meta?.totalPages ?? 1,
        },
      };
    } catch (error) {
      console.error('Error fetching filtered sales orders:', error);
      throw error;
    }
  },

  getSalesOrderById: async (id: number): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindOne(id);
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error fetching sales order ${id}:`, error);
      throw error;
    }
  },

  getSalesOrderComplete: async (id: number): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindCompleteOrder(id);
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error fetching complete sales order ${id}:`, error);
      throw error;
    }
  },

  getSaleFulfillmentDetail: async (orderId: number): Promise<SaleFulfillmentDetail> => {
    try {
      const response =
        await SalesOrdersMixedBranchFulfillmentService.saleFulfillmentControllerGetFulfillmentDetail(
          orderId,
        );
      return response as unknown as SaleFulfillmentDetail;
    } catch (error) {
      console.error(`Error fetching fulfillment detail for sales order ${orderId}:`, error);
      throw error;
    }
  },

  finalizeSaleFulfillment: async (orderId: number): Promise<SalesOrder> => {
    try {
      const response =
        await SalesOrdersMixedBranchFulfillmentService.saleFulfillmentControllerFinalizeFulfillment(
          orderId,
        );
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error finalizing fulfillment for sales order ${orderId}:`, error);
      throw error;
    }
  },

  updateOrderDelivery: async (
    orderId: number,
    items: Array<{ itemId: number; isDelivered: boolean }>,
  ): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerUpdateDelivery(orderId, {
        items,
      });
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error updating delivery for sales order ${orderId}:`, error);
      throw error;
    }
  },

  createSalesOrder: async (request: CreateSalesOrderWithItemsDto): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerCreateWithItems(request);
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error('Error creating sales order:', error);
      throw error;
    }
  },

  createMixedSale: async (request: CreateMixedSaleDto): Promise<SalesOrder> => {
    try {
      const response =
        await SalesOrdersMixedBranchFulfillmentService.saleFulfillmentControllerConfirmMixedSale(
          request,
        );
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error('Error creating mixed-branch sale:', error);
      throw error;
    }
  },

  updateSalesOrder: async (id: number, order: any): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerUpdate(id, order);
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error updating sales order ${id}:`, error);
      throw error;
    }
  },

  confirmSalesOrder: async (id: number): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerConfirmOrder(id);
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error confirming sales order ${id}:`, error);
      throw error;
    }
  },

  cancelSalesOrder: async (id: number): Promise<SalesOrder> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerCancelOrder(id);
      return mapAPISalesOrderToLocal(response);
    } catch (error) {
      console.error(`Error cancelling sales order ${id}:`, error);
      throw error;
    }
  },

  searchSalesOrders: async (query: string): Promise<SalesOrder[]> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerSearch(query);
      return response.map(mapAPISalesOrderToLocal);
    } catch (error) {
      console.error('Error searching sales orders:', error);
      throw error;
    }
  },

  getPendingSalesOrders: async (): Promise<SalesOrder[]> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerGetPendingOrders();
      return response.map(mapAPISalesOrderToLocal);
    } catch (error) {
      console.error('Error fetching pending sales orders:', error);
      throw error;
    }
  },

  getSalesOrdersByStatus: async (status: string): Promise<SalesOrder[]> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindByStatus(status);
      return response.map(mapAPISalesOrderToLocal);
    } catch (error) {
      console.error(`Error fetching sales orders by status ${status}:`, error);
      throw error;
    }
  },

  getSalesOrdersByLocation: async (locationId: number): Promise<SalesOrder[]> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindByLocation(locationId);
      return response.map(mapAPISalesOrderToLocal);
    } catch (error) {
      console.error(`Error fetching sales orders by location ${locationId}:`, error);
      throw error;
    }
  },

  getSalesOrdersByCustomer: async (customerId: number): Promise<SalesOrder[]> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerFindByCustomer(customerId);
      return response.map(mapAPISalesOrderToLocal);
    } catch (error) {
      console.error(`Error fetching sales orders by customer ${customerId}:`, error);
      throw error;
    }
  },

  // Sales Order Payments endpoints
  processPayment: async (request: CreateSalesOrderPaymentDto) => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerProcessPayment(request);
      return response;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  },

  getPaymentsByOrder: async (orderId: number) => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerGetPaymentsByOrder(orderId);
      return response;
    } catch (error) {
      console.error(`Error fetching payments for order ${orderId}:`, error);
      throw error;
    }
  },

  getPaymentSummary: async (orderId: number): Promise<PaymentSummaryDto> => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerGetPaymentSummary(orderId);
      return response;
    } catch (error) {
      console.error(`Error fetching payment summary for order ${orderId}:`, error);
      throw error;
    }
  },

  getPendingInstallments: async () => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerGetPendingInstallments();
      return response;
    } catch (error) {
      console.error('Error fetching pending installments:', error);
      throw error;
    }
  },

  getOverduePayments: async () => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerGetOverduePayments();
      return response;
    } catch (error) {
      console.error('Error fetching overdue payments:', error);
      throw error;
    }
  },

  updatePaymentStatus: async (paymentId: number) => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerUpdatePaymentStatus(paymentId);
      return response;
    } catch (error) {
      console.error(`Error updating payment status for payment ${paymentId}:`, error);
      throw error;
    }
  },

  // Sales Order Installments endpoints
  createInstallment: async (request: CreateSalesOrderInstallmentDto) => {
    try {
      const response =
        await SalesOrderInstallmentsService.salesOrderInstallmentsControllerCreate(request);
      return response;
    } catch (error) {
      console.error('Error creating installment:', error);
      throw error;
    }
  },

  generateInstallments: async (request: GenerateInstallmentsDto) => {
    try {
      const response =
        await SalesOrderInstallmentsService.salesOrderInstallmentsControllerGenerateInstallments(
          request,
        );
      return response;
    } catch (error) {
      console.error('Error generating installments:', error);
      throw error;
    }
  },

  getInstallmentsByOrder: async (orderId: number) => {
    try {
      const response =
        await SalesOrderInstallmentsService.salesOrderInstallmentsControllerFindByOrder(orderId);
      return response;
    } catch (error) {
      console.error(`Error fetching installments for order ${orderId}:`, error);
      throw error;
    }
  },

  getOverdueInstallments: async () => {
    try {
      const response =
        await SalesOrderInstallmentsService.salesOrderInstallmentsControllerFindOverdue();
      return response;
    } catch (error) {
      console.error('Error fetching overdue installments:', error);
      throw error;
    }
  },

  getUpcomingInstallments: async () => {
    try {
      const response =
        await SalesOrderInstallmentsService.salesOrderInstallmentsControllerFindUpcoming();
      return response;
    } catch (error) {
      console.error('Error fetching upcoming installments:', error);
      throw error;
    }
  },

  markInstallmentAsPaid: async (installmentId: number) => {
    try {
      const response =
        await SalesOrderInstallmentsService.salesOrderInstallmentsControllerMarkAsPaid(
          installmentId,
        );
      return response;
    } catch (error) {
      console.error(`Error marking installment ${installmentId} as paid:`, error);
      throw error;
    }
  },

  cancelInstallment: async (installmentId: number): Promise<void> => {
    try {
      await SalesOrderInstallmentsService.salesOrderInstallmentsControllerCancel(installmentId);
    } catch (error) {
      console.error(`Error cancelling installment ${installmentId}:`, error);
      throw error;
    }
  },

  // Dashboard and Statistics
  getSalesDashboard: async (): Promise<{
    totalSales: number;
    pendingOrders: number;
    overduePayments: number;
    recentOrders: SalesOrder[];
  }> => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerGetDashboard();
      // Map the response to expected format
      return {
        totalSales: 0,
        pendingOrders: response.partialPaidOrders?.length ?? 0,
        overduePayments: response.overdueOrders?.length ?? 0,
        recentOrders: response.recentOrders?.map(mapAPISalesOrderToLocal) ?? [],
      };
    } catch (error) {
      console.error('Error fetching sales dashboard:', error);
      throw error;
    }
  },

  getSalesStats: async (startDate: string, endDate: string) => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerGetSalesStats(
        startDate,
        endDate,
      );
      return response;
    } catch (error) {
      console.error('Error fetching sales stats:', error);
      throw error;
    }
  },

  getPaymentStatistics: async (startDate: string, endDate: string) => {
    try {
      const response =
        await SalesOrderPaymentsService.salesOrderPaymentsControllerGetPaymentStatistics(
          startDate,
          endDate,
        );
      return response;
    } catch (error) {
      console.error('Error fetching payment statistics:', error);
      throw error;
    }
  },

  // Utility methods for the frontend
  getTopCustomers: async (limit: number = 10): Promise<CustomerProfile[]> => {
    try {
      const response =
        await CustomerProfilesService.customerProfilesControllerGetTopCustomers(limit);
      return response.map(mapAPICustomerToLocal);
    } catch (error) {
      console.error('Error fetching top customers:', error);
      throw error;
    }
  },

  getCustomersByTier: async (tier: string): Promise<CustomerProfile[]> => {
    try {
      const response =
        await CustomerProfilesService.customerProfilesControllerGetCustomersByTier(tier);
      return response.map(mapAPICustomerToLocal);
    } catch (error) {
      console.error(`Error fetching customers by tier ${tier}:`, error);
      throw error;
    }
  },

  getCustomerByEmail: async (email: string): Promise<CustomerProfile | null> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerFindByEmail(email);
      return mapAPICustomerToLocal(response);
    } catch (error) {
      console.error(`Error fetching customer by email ${email}:`, error);
      return null;
    }
  },

  getCustomerByPhone: async (phone: string): Promise<CustomerProfile | null> => {
    try {
      const response = await CustomerProfilesService.customerProfilesControllerFindByPhone(phone);
      return mapAPICustomerToLocal(response);
    } catch (error) {
      console.error(`Error fetching customer by phone ${phone}:`, error);
      return null;
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  redeemCustomerPoints: async (customerId: number, points: number) => {
    try {
      const response =
        await CustomerProfilesService.customerProfilesControllerRedeemLoyaltyPoints(customerId);
      return response;
    } catch (error) {
      console.error(`Error redeeming points for customer ${customerId}:`, error);
      throw error;
    }
  },

  updateCustomerPurchaseStats: async (customerId: number): Promise<CustomerProfile> => {
    try {
      const response =
        await CustomerProfilesService.customerProfilesControllerUpdatePurchaseStats(customerId);
      return mapAPICustomerToLocal(response);
    } catch (error) {
      console.error(`Error updating purchase stats for customer ${customerId}:`, error);
      throw error;
    }
  },

  getCustomerSalesHistory: async (
    customerId: number,
    params?: { page?: number; limit?: number },
  ): Promise<{
    data: CustomerSalesHistoryItem[];
    total: number;
    page: number;
    limit: number;
  }> => {
    try {
      const state = store.getState();
      const token = state.auth.token ?? '';

      if (token === '') {
        throw new Error('No se encontró token de autenticación.');
      }

      const { data: payload } = await axios.get<{
        data?: unknown[];
        total?: unknown;
        page?: unknown;
        limit?: unknown;
      }>(`${OpenAPI.BASE}/customer-profiles/${customerId}/sales-history`, {
        params: {
          ...(params?.page != null ? { page: params.page } : {}),
          ...(params?.limit != null ? { limit: params.limit } : {}),
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const rawRows = Array.isArray(payload?.data) ? payload.data : [];
      return {
        data: rawRows.map(mapCustomerSalesHistoryRow),
        total: Number(payload?.total ?? 0),
        page: Number(payload?.page ?? params?.page ?? 1),
        limit: Number(payload?.limit ?? params?.limit ?? 20),
      };
    } catch (error: unknown) {
      console.error(`Error fetching sales history for customer ${customerId}:`, error);
      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        const msgFromBody =
          data != null &&
          typeof data === 'object' &&
          'message' in data &&
          typeof (data as { message: unknown }).message === 'string'
            ? (data as { message: string }).message
            : undefined;
        throw new Error(
          msgFromBody ??
            error.message ??
            `Error ${String(error.response?.status ?? '')}: ${String(error.response?.statusText ?? '')}`,
        );
      }
      throw error;
    }
  },

  // Return/Refund endpoints
  checkReturnEligibility: async (orderId: number) => {
    try {
      // NOTE: Frontend bypasses eligibility check - backend handles all validation
      // This method is kept for backward compatibility but not used in return flow
      const response =
        await SalesOrdersService.salesOrdersControllerCheckReturnEligibility(orderId);
      return response;
    } catch (error) {
      console.error(`Error checking return eligibility for order ${orderId}:`, error);
      throw error;
    }
  },

  returnFullOrder: async (orderId: number, returnData: ReturnFormData) => {
    try {
      const response = await SalesOrdersService.salesOrdersControllerReturnOrder(
        orderId,
        toReturnSalesOrderDto(returnData),
      );
      return response;
    } catch (error) {
      console.error(`Error processing return for order ${orderId}:`, error);
      throw error;
    }
  },

  returnPartialOrder: async (orderId: number, returnData: PartialReturnSalesOrderDto) => {
    try {
      // Actor is resolved from JWT on the server — never send processedById.
      const response = await SalesOrdersService.salesOrdersControllerPartialReturnOrder(orderId, {
        items: returnData.items,
        returnNotes: returnData.returnNotes,
        refundReference: returnData.refundReference,
        refundNotes: returnData.refundNotes,
      });
      return response;
    } catch (error) {
      console.error(`Error processing partial return for order ${orderId}:`, error);
      throw error;
    }
  },

  // Exchange endpoints
  checkExchangeEligibility: async (orderId: number) => {
    try {
      // Use fetch directly since API client might not have this method yet
      const apiBaseUrl = OpenAPI.BASE;
      // Get token from Redux store (same way as API client)
      const state = store.getState();
      const token = state.auth.token ?? '';

      if (token === '') {
        throw new Error('No se encontró token de autenticación. Inicie sesión de nuevo.');
      }

      const response = await fetch(`${apiBaseUrl}/sales-orders/${orderId}/exchange-eligibility`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error checking exchange eligibility for order ${orderId}:`, error);
      throw error;
    }
  },

  processExchange: async (orderId: number, exchangeData: Record<string, unknown>) => {
    try {
      const apiBaseUrl = OpenAPI.BASE;
      const state = store.getState();
      const token = state.auth.token ?? '';

      if (token === '') {
        throw new Error('No se encontró token de autenticación. Inicie sesión de nuevo.');
      }

      if (state.auth.user?.id == null) {
        throw new Error(
          'Sesión inválida: no se pudo identificar al usuario. Vuelve a iniciar sesión.',
        );
      }

      const response = await fetch(`${apiBaseUrl}/sales-orders/${orderId}/exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(exchangeData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error processing exchange for order ${orderId}:`, error);
      throw error;
    }
  },

  getPostSalesCounts: async (
    locationId?: number,
  ): Promise<{
    total: number;
    pendientesDePago: number;
    completadas: number;
    devueltas: number;
    intercambios: number;
  }> => {
    try {
      const apiBaseUrl = OpenAPI.BASE;
      const state = store.getState();
      const token = state.auth.token ?? '';

      if (token === '') {
        throw new Error('No se encontró token de autenticación.');
      }

      const url = new URL(`${apiBaseUrl}/sales-orders/post-sales-counts`);
      if (locationId != null) {
        url.searchParams.set('locationId', String(locationId));
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching post-sales counts:', error);
      throw error;
    }
  },

  getOrderProductHistory: async (orderId: number) => {
    try {
      const apiBaseUrl = OpenAPI.BASE;
      const state = store.getState();
      const token = state.auth.token ?? '';

      if (token === '') {
        throw new Error('No se encontró token de autenticación.');
      }

      const response = await fetch(`${apiBaseUrl}/sales-orders/${orderId}/product-history`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error: any) {
      console.error(`Error fetching product history for order ${orderId}:`, error);
      throw error;
    }
  },
};

// Export the service for backward compatibility
export const salesService = salesApi;
export default salesApi;
